/**
 * Tone vocabulary mirrors {@link StatusPill} on purpose so callers don't
 * have to learn a second palette. The visual language is intentionally
 * different though — see the styled file for the rationale.
 */
export const MODE_BADGE_TONE_NEUTRAL = 'neutral';
export const MODE_BADGE_TONE_PRIMARY = 'primary';
export const MODE_BADGE_TONE_SUCCESS = 'success';
export const MODE_BADGE_TONE_CAUTION = 'caution';
export const MODE_BADGE_TONE_ERROR = 'error';
export const MODE_BADGE_TONE_INFO = 'info';

export type ModeBadgeTone =
  | typeof MODE_BADGE_TONE_NEUTRAL
  | typeof MODE_BADGE_TONE_PRIMARY
  | typeof MODE_BADGE_TONE_SUCCESS
  | typeof MODE_BADGE_TONE_CAUTION
  | typeof MODE_BADGE_TONE_ERROR
  | typeof MODE_BADGE_TONE_INFO;

/**
 * Two sizes — `sm` for inline-with-text use (toolbars, breadcrumbs); `md`
 * for standalone placement (header right corner, page banner).
 */
export const MODE_BADGE_SIZE_SM = 'sm';
export const MODE_BADGE_SIZE_MD = 'md';

export type ModeBadgeSize =
  | typeof MODE_BADGE_SIZE_SM
  | typeof MODE_BADGE_SIZE_MD;

export const MODE_BADGE_SIZE_TOKENS: Record<
  ModeBadgeSize,
  { height: string; padX: string; fontSize: string; gap: string }
> = {
  [MODE_BADGE_SIZE_SM]: {
    height: '20px',
    padX: '8px',
    fontSize: '11.5px',
    gap: '6px',
  },
  [MODE_BADGE_SIZE_MD]: {
    height: '24px',
    padX: '10px',
    fontSize: '12.5px',
    gap: '6px',
  },
};

/**
 * Visual treatment.
 *
 *   - `pill` (default): tinted BORDER + sentence-case label + optional dot.
 *     The "high-emphasis chip" read used in headers / page banners.
 *   - `flat`: no border, transparent fill, near-square 8px radius, an
 *     UPPERCASE letter-spaced label that picks up the tone colour. The
 *     "lives inside another toolbar" read used by the floating canvas
 *     toolbar (see `.ctb__mode` in the prototype). Drops the dot by default
 *     since the typographic treatment carries the tone signal.
 */
export const MODE_BADGE_VARIANT_PILL = 'pill';
export const MODE_BADGE_VARIANT_FLAT = 'flat';

export type ModeBadgeVariant =
  | typeof MODE_BADGE_VARIANT_PILL
  | typeof MODE_BADGE_VARIANT_FLAT;
